import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "./Services.css";

function Services() {
  const navigate = useNavigate();

  const siteUrl = "https://www.samsoftix.com";
  const pageUrl = `${siteUrl}/all_services`;

  const handleBookingClick = () => {
    navigate("/serviceBooking");
  };

  const servicesData = [
    {
      title: "Website Development",
      desc: "Responsive, SEO-friendly websites with modern UI/UX for businesses, portfolios, service companies, and ecommerce brands.",
    },
    {
      title: "Mobile App Development",
      desc: "Custom iOS, Android, and cross-platform mobile applications designed for performance, usability, and scalability.",
    },
    {
      title: "AI & Machine Learning Solutions",
      desc: "AI and machine learning solutions including automation, predictive analytics, intelligent workflows, and data-driven applications.",
    },
    {
      title: "Chatbot Development",
      desc: "AI-driven chatbots for customer support, lead generation, FAQs, business communication, and website engagement.",
    },
    {
      title: "E-commerce Solutions",
      desc: "Online stores with product management, ecommerce functionality, payment integrations, order workflows, and business tools.",
    },
    {
      title: "SEO & Digital Marketing",
      desc: "SEO, local SEO, content optimization, social media support, and digital marketing services to improve online visibility.",
    },
    {
      title: "Cloud & DevOps",
      desc: "Cloud deployment, CI/CD workflows, infrastructure configuration, automation, and scalable application environments.",
    },
    {
      title: "UI/UX Design",
      desc: "Modern and intuitive UI/UX design focused on usability, visual consistency, responsive experiences, and user engagement.",
    },
    {
      title: "API Integration",
      desc: "Integration of APIs, payment systems, CRMs, logistics platforms, analytics tools, and other business services.",
    },
    {
      title: "Cybersecurity Services",
      desc: "Security-focused technical services including vulnerability assessment, secure configuration, and application security support.",
    },
    {
      title: "Data Management & Analytics",
      desc: "Data cleaning, organization, transformation, management, and analytics to support better business decisions.",
    },
    {
      title: "Custom Software Development",
      desc: "Custom software solutions including ERP, CRM, inventory, workflow management, dashboards, and business systems.",
    },
    {
      title: "Game Development",
      desc: "Interactive 2D and 3D games for web, mobile, and desktop platforms using modern game development technologies.",
    },
    {
      title: "Cloud Hosting & Domain Management",
      desc: "Website hosting setup, SSL configuration, domain management, deployment assistance, and server optimization.",
    },
    {
      title: "Technical Consultation",
      desc: "Technical guidance covering technology selection, system architecture, software planning, and digital transformation.",
    },
    {
      title: "Dataset Creation & Annotation",
      desc: "Structured datasets and annotation support for AI and machine learning applications.",
    },
    {
      title: "IT Lab Setup for Education",
      desc: "IT laboratory planning and setup for schools, colleges, institutes, and training centers.",
    },
    {
      title: "Hardware Maintenance & Support",
      desc: "Computer, server, and IT equipment maintenance, troubleshooting, configuration, and technical support.",
    },
  ];

  /* =========================================================
     Structured Data
     ========================================================= */

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: siteUrl,
        name: "SamSoftix",
        description:
          "SamSoftix is a software house and training center providing website development, mobile apps, ecommerce, AI, SEO, custom software, IT solutions, and technical training.",
        inLanguage: "en-PK",
      },

      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": `${siteUrl}/#business`,
        name: "SamSoftix",
        url: siteUrl,
        telephone: "+92-332-0472184",
        email: "info@samsoftix.com",
        description:
          "SamSoftix provides website development, mobile app development, ecommerce, AI solutions, SEO, custom software, cloud services, IT support, and digital solutions from Wan, Sambrial, Punjab, Pakistan, with remote services for international clients.",

        address: {
          "@type": "PostalAddress",
          addressLocality: "Wan",
          addressRegion: "Punjab",
          addressCountry: "PK",
        },

        areaServed: [
          {
            "@type": "City",
            name: "Wan",
          },
          {
            "@type": "City",
            name: "Sambrial",
          },
          {
            "@type": "City",
            name: "Sialkot",
          },
          {
            "@type": "AdministrativeArea",
            name: "Punjab",
          },
          {
            "@type": "Country",
            name: "Pakistan",
          },
          {
            "@type": "Country",
            name: "United Arab Emirates",
          },
          {
            "@type": "Country",
            name: "Saudi Arabia",
          },
          {
            "@type": "Country",
            name: "United States",
          },
          {
            "@type": "Country",
            name: "United Kingdom",
          },
        ],

        sameAs: [
          "https://www.facebook.com/samsoftix",
          "https://www.linkedin.com/company/samsoftix",
          "https://www.instagram.com/samsoftix",
        ],
      },

      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Digital Services & Software Solutions | SamSoftix",
        description:
          "Explore SamSoftix website development, mobile app development, AI, ecommerce, SEO, custom software, cloud, UI/UX, data, IT support, and digital services.",
        isPartOf: {
          "@id": `${siteUrl}/#website`,
        },
        about: {
          "@id": `${siteUrl}/#business`,
        },
        inLanguage: "en-PK",
        breadcrumb: {
          "@id": `${pageUrl}#breadcrumb`,
        },
      },

      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: pageUrl,
          },
        ],
      },

      {
        "@type": "OfferCatalog",
        "@id": `${pageUrl}#services`,
        name: "SamSoftix IT & Software Services",
        description:
          "Website development, mobile applications, AI, ecommerce, SEO, custom software, cloud, UI/UX, data, IT support, and digital services.",
        provider: {
          "@id": `${siteUrl}/#business`,
        },
        itemListElement: servicesData.map((service, index) => ({
          "@type": "Offer",
          position: index + 1,
          itemOffered: {
            "@type": "Service",
            name: service.title,
            description: service.desc,
            provider: {
              "@id": `${siteUrl}/#business`,
            },
          },
        })),
      },
    ],
  };

  /* =========================================================
     Fade-in Animation
     ========================================================= */

  useEffect(() => {
    const sections = document.querySelectorAll(".fade-section");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <main className="services-page">

      <Helmet>
        {/* =====================================================
            BASIC SEO
            ===================================================== */}

        <html lang="en-PK" />

        <title>
          IT & Software Services | Web, Mobile, AI & SEO | SamSoftix
        </title>

        <meta
          name="description"
          content="Explore SamSoftix IT and software services including website development, mobile apps, AI/ML, ecommerce, SEO, UI/UX, custom software, cloud solutions, data services and IT support in Pakistan and worldwide."
        />

        <meta
          name="keywords"
          content="SamSoftix services, website development Pakistan, website development Sambrial, web development Sialkot, software house Pakistan, mobile app development Pakistan, React development, Next.js development, Flutter app development, ecommerce development Pakistan, AI solutions Pakistan, machine learning services, SEO services Pakistan, local SEO Sambrial, digital marketing Pakistan, custom software development, UI UX design, cloud services, IT support Pakistan, game development, IT lab setup, data management"
        />

        <meta
          name="author"
          content="SamSoftix"
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <meta
          name="googlebot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        {/* =====================================================
            CANONICAL
            ===================================================== */}

        <link
          rel="canonical"
          href={pageUrl}
        />

        {/* =====================================================
            GEO / LOCAL SEO
            ===================================================== */}

        <meta
          name="geo.region"
          content="PK-PB"
        />

        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        {/* =====================================================
            OPEN GRAPH
            ===================================================== */}

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:title"
          content="IT & Software Services | SamSoftix"
        />

        <meta
          property="og:description"
          content="Website development, mobile apps, AI, ecommerce, SEO, custom software, UI/UX, cloud, data and IT solutions from SamSoftix."
        />

        <meta
          property="og:url"
          content={pageUrl}
        />

        <meta
          property="og:site_name"
          content="SamSoftix"
        />

        <meta
          property="og:locale"
          content="en_PK"
        />

        {/* =====================================================
            TWITTER / X
            ===================================================== */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="IT & Software Services | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Explore SamSoftix website, mobile app, AI, ecommerce, SEO, custom software, cloud and IT services."
        />

        {/* =====================================================
            THEME
            ===================================================== */}

        <meta
          name="theme-color"
          content="#06111f"
        />

        {/* =====================================================
            STRUCTURED DATA
            ===================================================== */}

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
          ===================================================== */}

      <header
        className="services-hero fade-section"
        style={{
          opacity: 0,
          transform: "translateY(20px)",
        }}
      >
        <span className="services-kicker">
          SAMSOFTIX • IT & SOFTWARE SOLUTIONS
        </span>

        <h1>
          Professional IT & Software Services
        </h1>

        <p>
          Website development, mobile applications, AI solutions,
          ecommerce, SEO, custom software, cloud services and
          technology solutions for businesses and organizations.
        </p>

        <p className="global-note">
          🌍 Serving clients in Pakistan and international clients
          through remote collaboration.
        </p>

        <div className="services-location-tags">
          <span>Wan</span>
          <span>Sambrial</span>
          <span>Sialkot</span>
          <span>Pakistan</span>
          <span>Remote Worldwide</span>
        </div>

        <button
          className="book-btn"
          onClick={handleBookingClick}
        >
          Book Your Service
        </button>
      </header>

      {/* =====================================================
          REMOTE SERVICES
          ===================================================== */}

      <section
        className="remote-section fade-section"
        style={{
          opacity: 0,
          transform: "translateY(20px)",
        }}
      >
        <span className="services-section-label">
          REMOTE DEVELOPMENT
        </span>

        <h2>
          Flexible Digital Solutions for Local & International Clients
        </h2>

        <p>
          Collaborate with the SamSoftix team online for software
          development, website projects, mobile applications, digital
          services and technical solutions. Project communication,
          requirements and deliverables can be managed remotely.
        </p>
      </section>

      {/* =====================================================
          SERVICES LIST
          ===================================================== */}

      <section
        className="services-list fade-section"
        style={{
          opacity: 0,
          transform: "translateY(20px)",
        }}
      >
        <div className="services-heading">
          <span className="services-section-label">
            OUR SERVICES
          </span>

          <h2>
            Technology Services for Businesses & Organizations
          </h2>

          <p>
            Explore our range of software development, digital,
            data and IT services.
          </p>
        </div>

        <div className="services-grid">
          {servicesData.map((service, index) => (
            <article
              key={service.title}
              className="service-box"
            >
              <span className="service-number">
                {String(index + 1).padStart(2, "0")}
              </span>

              <h3>
                {service.title}
              </h3>

              <p>
                {service.desc}
              </p>

              <button
                className="service-book-btn"
                onClick={handleBookingClick}
              >
                Discuss This Service
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* =====================================================
          CTA
          ===================================================== */}

      <section
        className="book-service-section fade-section"
        style={{
          opacity: 0,
          transform: "translateY(20px)",
        }}
      >
        <span className="services-section-label">
          START YOUR PROJECT
        </span>

        <h2>
          Have a Project in Mind?
        </h2>

        <p>
          Share your requirements with SamSoftix and discuss the
          technology, scope and development approach suitable for
          your project.
        </p>

        <button
          className="book-btn"
          onClick={handleBookingClick}
        >
          Request a Service
        </button>
      </section>

    </main>
  );
}

export default Services;